import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Link2,
    Loader2,
    CheckCircle2,
    XCircle,
    Search,
    Download,
    History,
    MessageSquare,
    ListTodo,
    ChevronDown,
    ChevronRight,
    Mail,
    Folder
} from 'lucide-react';
import {
    subscribeUserIntegrations,
    subscribeIntegrationImports,
    getGoogleDriveAuthUrlAPI,
    getMicrosoftAuthUrlAPI,
    disconnectGoogleDriveAPI,
    disconnectMicrosoftAPI,
    setPendingOAuthProvider,
    listGoogleDriveFilesAPI,
    importGoogleDriveFilesAPI,
    listOneDriveFilesAPI,
    importOneDriveFilesAPI,
    searchSharePointSitesAPI,
    listSharePointDrivesAPI,
    listGraphDriveItemsAPI,
    listOneNoteNotebooksAPI,
    listOneNoteSectionsAPI,
    listOneNotePagesAPI,
    importOneNotePagesAPI,
    listMicrosoftTeamsAPI,
    listMicrosoftTeamChannelsAPI,
    listTeamChannelMessagesAPI,
    importTeamChannelMessagesAPI,
    listMicrosoftChatsAPI,
    listChatMessagesAPI,
    importChatMessagesAPI,
    listGmailMessagesAPI,
    importGmailMessagesAPI,
    listGoogleChatSpacesAPI,
    listGoogleChatMessagesAPI,
    importGoogleChatMessagesAPI,
    listGoogleTaskListsAPI,
    listGoogleTasksAPI,
    importGoogleTasksAPI
} from '../services/firebase';
import ragService from '../services/ragService';

const OAUTH_ERR_HINT =
    'If this fails immediately, the Cloud Function may be missing client ID and redirect URI environment variables.';

const GMAIL_ERR_HINT =
    'For Gmail: in Google Cloud → APIs & Services → enable Gmail API; Open OAuth consent screen → add scope Gmail / gmail.readonly; if the app is in Testing, add your Google account as a Test user. Then Disconnect and Connect Google in Integrations again.';

/** Maps friendly filters to Gmail’s `q` search string. */
function buildGmailSearchQuery({ timePreset, fromInput, subjectInput, hasAttachment, advancedRaw }) {
    const adv = (advancedRaw || '').trim();
    if (adv) {
        return adv;
    }
    const parts = [];
    if (timePreset && timePreset !== 'all') {
        parts.push(`newer_than:${timePreset}`);
    }
    const addresses = (fromInput || '')
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
    for (const addr of addresses) {
        parts.push(`from:${addr}`);
    }
    const subj = (subjectInput || '').trim();
    if (subj) {
        const safe = subj.replace(/"/g, '');
        parts.push(safe.includes(' ') ? `subject:"${safe}"` : `subject:${safe}`);
    }
    if (hasAttachment) {
        parts.push('has:attachment');
    }
    let q = parts.join(' ').trim();
    if (!q) {
        if (timePreset === 'all') {
            return '';
        }
        return 'newer_than:30d';
    }
    return q;
}

function providerLabel(provider) {
    switch (provider) {
        case 'googleDrive':
            return 'Drive';
        case 'gmail':
            return 'Gmail';
        case 'googleChat':
            return 'Chat';
        case 'googleTasks':
            return 'Tasks';
        case 'oneDrive':
            return 'OneDrive';
        case 'sharePoint':
            return 'SharePoint';
        case 'oneNote':
            return 'OneNote';
        case 'teamsChannel':
            return 'Teams (channel)';
        case 'teamsChat':
            return 'Teams (chat)';
        default:
            return provider || '—';
    }
}

function formatDocDate(d) {
    const t = d?.createdAt?.toDate?.() || d?.uploadedAt?.toDate?.();
    return t ? t.toLocaleString() : '—';
}

const GOOGLE_FAV = {
    drive: 'https://www.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png',
    mail: 'https://www.gstatic.com/images/branding/product/1x/gmail_2020q4_32dp.png',
    chat: 'https://www.gstatic.com/images/branding/product/1x/chat_2020q4_32dp.png',
    tasks: 'https://www.gstatic.com/images/branding/product/1x/tasks_2022q1_32dp.png'
};

const GOOGLE_CHAT_LOGO_48 =
    'https://www.gstatic.com/images/branding/product/1x/chat_2020q4_48dp.png';

const MS365_FAV = {
    suite: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/office_48x1.png',
    onedrive: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/onedrive_48x1.png',
    sharepoint: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/sharepoint_48x1.png',
    onenote: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/onenote_48x1.png',
    teams: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/teams_48x1.png',
    word: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/word_48x1.png',
    excel: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/excel_48x1.png',
    powerpoint: 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/png/powerpoint_48x1.png'
};

function ImportSourceBadge({ provider }) {
    const src =
        provider === 'googleDrive'
            ? GOOGLE_FAV.drive
            : provider === 'gmail'
              ? GOOGLE_FAV.mail
              : provider === 'googleChat'
                ? GOOGLE_FAV.chat
                : provider === 'googleTasks'
                  ? GOOGLE_FAV.tasks
                  : provider === 'oneDrive'
                    ? MS365_FAV.onedrive
                    : provider === 'sharePoint'
                      ? MS365_FAV.sharepoint
                      : provider === 'oneNote'
                        ? MS365_FAV.onenote
                        : provider === 'teamsChannel' || provider === 'teamsChat'
                          ? MS365_FAV.teams
                          : null;
    return (
        <span className="inline-flex items-center gap-2 min-w-0">
            {src ? (
                <img src={src} alt="" className="h-4 w-4 flex-shrink-0 object-contain" />
            ) : null}
            <span className="truncate">{providerLabel(provider)}</span>
        </span>
    );
}

function GoogleTabButton({ label, active, onClick, src, IconFallback }) {
    const [imgOk, setImgOk] = useState(true);
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-1 min-w-0 items-center justify-center gap-1.5 sm:gap-2 rounded-lg px-2 sm:px-3 py-2.5 text-xs sm:text-sm font-medium transition-all ${
                active
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
            {imgOk ? (
                <img
                    src={src}
                    alt=""
                    className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 object-contain"
                    onError={() => setImgOk(false)}
                />
            ) : (
                <IconFallback className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            )}
            <span className="truncate">{label}</span>
        </button>
    );
}

const IntegrationsTab = ({ user, projects }) => {
    const [summary, setSummary] = useState(null);
    const [localError, setLocalError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [driveQuery, setDriveQuery] = useState('');
    const [driveFiles, setDriveFiles] = useState([]);
    const [driveNext, setDriveNext] = useState(null);
    const [selectedDrive, setSelectedDrive] = useState(() => new Set());
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [importLog, setImportLog] = useState(null);
    const [gmailTimePreset, setGmailTimePreset] = useState('30d');
    const [gmailFrom, setGmailFrom] = useState('');
    const [gmailSubject, setGmailSubject] = useState('');
    const [gmailHasAttachment, setGmailHasAttachment] = useState(false);
    const [gmailAdvanced, setGmailAdvanced] = useState('');
    const [gmailMessages, setGmailMessages] = useState([]);
    const [gmailNext, setGmailNext] = useState(null);
    const [selectedGmail, setSelectedGmail] = useState(() => new Set());

    const [importHistory, setImportHistory] = useState([]);
    const [importHistoryOpen, setImportHistoryOpen] = useState(false);
    const [googleSubTab, setGoogleSubTab] = useState('drive');

    const [chatSpaces, setChatSpaces] = useState([]);
    const [chatSpaceNext, setChatSpaceNext] = useState(null);
    const [selectedChatSpace, setSelectedChatSpace] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [chatMsgNext, setChatMsgNext] = useState(null);
    const [selectedChatMsg, setSelectedChatMsg] = useState(() => new Set());

    const [taskLists, setTaskLists] = useState([]);
    const [selectedTaskListId, setSelectedTaskListId] = useState('');
    const [selectedTaskListTitle, setSelectedTaskListTitle] = useState('');
    const [googleTasks, setGoogleTasks] = useState([]);
    const [showCompletedTasks, setShowCompletedTasks] = useState(true);
    const [selectedGoogleTasks, setSelectedGoogleTasks] = useState(() => new Set());

    const [msSubTab, setMsSubTab] = useState('onedrive');
    const [oDriveQuery, setODriveQuery] = useState('');
    const [oDriveFiles, setODriveFiles] = useState([]);
    const [oDriveNext, setODriveNext] = useState(null);
    const [oDriveFolderId, setODriveFolderId] = useState(null);
    const [oDriveStack, setODriveStack] = useState([{ id: null, name: 'OneDrive' }]);
    const [selectedODrive, setSelectedODrive] = useState(() => new Set());

    const [spQuery, setSpQuery] = useState('');
    const [spSites, setSpSites] = useState([]);
    const [spSiteId, setSpSiteId] = useState('');
    const [spDrives, setSpDrives] = useState([]);
    const [spDriveId, setSpDriveId] = useState('');
    const [spListQ, setSpListQ] = useState('');
    const [spFiles, setSpFiles] = useState([]);
    const [spNext, setSpNext] = useState(null);
    const [selectedSp, setSelectedSp] = useState(() => new Set());

    const [onNotebooks, setOnNotebooks] = useState([]);
    const [onNbId, setOnNbId] = useState('');
    const [onSections, setOnSections] = useState([]);
    const [onSecId, setOnSecId] = useState('');
    const [onPages, setOnPages] = useState([]);
    const [onPageNext, setOnPageNext] = useState(null);
    const [selectedOnPages, setSelectedOnPages] = useState(() => new Set());

    const [teamsView, setTeamsView] = useState('channels');
    const [msTeams, setMsTeams] = useState([]);
    const [tcTeamId, setTcTeamId] = useState('');
    const [tcChannels, setTcChannels] = useState([]);
    const [tcChannelId, setTcChannelId] = useState('');
    const [tcMsgs, setTcMsgs] = useState([]);
    const [tcMsgNext, setTcMsgNext] = useState(null);
    const [selectedTc, setSelectedTc] = useState(() => new Set());
    const [msChats, setMsChats] = useState([]);
    const [tChatId, setTChatId] = useState('');
    const [tChatMsgs, setTChatMsgs] = useState([]);
    const [tChatNext, setTChatNext] = useState(null);
    const [selectedTChat, setSelectedTChat] = useState(() => new Set());

    const gmailSearchQ = useMemo(
        () =>
            buildGmailSearchQuery({
                timePreset: gmailTimePreset,
                fromInput: gmailFrom,
                subjectInput: gmailSubject,
                hasAttachment: gmailHasAttachment,
                advancedRaw: gmailAdvanced
            }),
        [gmailTimePreset, gmailFrom, gmailSubject, gmailHasAttachment, gmailAdvanced]
    );

    useEffect(() => {
        if (!user?.uid) {
            setSummary(null);
            return;
        }
        const unsub = subscribeUserIntegrations(user.uid, (data) => setSummary(data));
        return () => unsub();
    }, [user?.uid]);

    useEffect(() => {
        if (!user?.uid) {
            setImportHistory([]);
            return;
        }
        return subscribeIntegrationImports(user.uid, (rows) => setImportHistory(rows));
    }, [user?.uid]);

    const google = summary?.googleDrive;
    const ms = summary?.microsoft365;

    useEffect(() => {
        if (!ms?.connected) {
            setODriveFolderId(null);
            setODriveStack([{ id: null, name: 'OneDrive' }]);
            setODriveFiles([]);
            setODriveNext(null);
            setSelectedODrive(new Set());
        }
    }, [ms?.connected]);

    const errorLooksGmailRelated =
        localError && /gmail|gmail\.readonly|gmail api/i.test(String(localError));

    const projectOptions = useMemo(() => {
        const list = Array.isArray(projects) ? projects : [];
        return list
            .filter((p) => p && p.id && !p.archived)
            .map((p) => ({ id: p.id, name: p.name || p.title || 'Untitled Project' }));
    }, [projects]);

    const projectNameById = useMemo(() => {
        const m = {};
        projectOptions.forEach((p) => {
            m[p.id] = p.name;
        });
        return m;
    }, [projectOptions]);

    const goOAuth = useCallback(
        async (which) => {
            setLocalError(null);
            setBusy(which);
            try {
                setPendingOAuthProvider(which);
                const res =
                    which === 'google' ? await getGoogleDriveAuthUrlAPI() : await getMicrosoftAuthUrlAPI();
                if (!res?.authUrl) {
                    throw new Error('No auth URL returned');
                }
                window.location.assign(res.authUrl);
            } catch (e) {
                setLocalError(
                    e?.message || (typeof e === 'string' ? e : 'Could not start authorization')
                );
                setBusy(null);
            }
        },
        []
    );

    const onDisconnect = async (which) => {
        setLocalError(null);
        setBusy(`dis-${which}`);
        try {
            if (which === 'google') {
                await disconnectGoogleDriveAPI();
            } else {
                await disconnectMicrosoftAPI();
            }
        } catch (e) {
            setLocalError(e?.message || 'Disconnect failed');
        } finally {
            setBusy(null);
        }
    };

    const loadDriveFiles = useCallback(
        async (mode) => {
            if (!google?.connected) {
                setLocalError('Connect Google Drive first.');
                return;
            }
            setLocalError(null);
            setBusy('drive-list');
            try {
                const res = await listGoogleDriveFilesAPI({
                    q: driveQuery,
                    pageToken: mode === 'more' ? driveNext : null,
                    pageSize: 20
                });
                const newFiles = Array.isArray(res?.files) ? res.files : [];
                setDriveFiles((prev) => (mode === 'more' ? [...prev, ...newFiles] : newFiles));
                setDriveNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not load Drive files');
            } finally {
                setBusy(null);
            }
        },
        [google?.connected, driveQuery, driveNext]
    );

    const toggleDriveFile = (id) => {
        setSelectedDrive((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runImport = async () => {
        setLocalError(null);
        setImportLog(null);
        const fileIds = Array.from(selectedDrive);
        if (!google?.connected) {
            setLocalError('Connect Google Drive first.');
            return;
        }
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        if (fileIds.length === 0) {
            setLocalError('Select at least one file to import.');
            return;
        }
        setBusy('drive-import');
        try {
            const res = await importGoogleDriveFilesAPI({ projectId: selectedProjectId, fileIds });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Imported file',
                    content: d.content,
                    type: 'document',
                    source: 'googleDrive',
                    link: d.webViewLink || null
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(
                `Imported ${docs.length} file(s). Embedded ${embedded} into the knowledge base.`
            );
            setSelectedDrive(new Set());
        } catch (e) {
            setLocalError(e?.message || 'Import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadOneDriveFiles = useCallback(
        async (mode, nav = {}) => {
            if (!ms?.connected) {
                setLocalError('Connect Microsoft 365 first.');
                return;
            }
            setLocalError(null);
            setBusy('ms-od-list');
            try {
                const folderIdForApi =
                    mode === 'more'
                        ? oDriveFolderId
                        : nav.folderId !== undefined
                          ? nav.folderId
                          : oDriveFolderId;
                const res = await listOneDriveFilesAPI({
                    q: oDriveQuery,
                    pageToken: mode === 'more' ? oDriveNext : null,
                    pageSize: 50,
                    ...(folderIdForApi ? { folderId: folderIdForApi } : {})
                });
                const newFiles = Array.isArray(res?.files) ? res.files : [];
                setODriveFiles((prev) => (mode === 'more' ? [...prev, ...newFiles] : newFiles));
                setODriveNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not load OneDrive files');
            } finally {
                setBusy(null);
            }
        },
        [ms?.connected, oDriveQuery, oDriveNext, oDriveFolderId]
    );

    const openODriveFolder = useCallback(
        (f) => {
            setODriveStack((s) => [...s, { id: f.id, name: f.name }]);
            setODriveFolderId(f.id);
            setODriveNext(null);
            setODriveFiles([]);
            setSelectedODrive(new Set());
            loadOneDriveFiles('fresh', { folderId: f.id });
        },
        [loadOneDriveFiles]
    );

    const goODriveCrumb = useCallback(
        (index) => {
            const crumb = oDriveStack[index];
            if (!crumb) return;
            const newStack = oDriveStack.slice(0, index + 1);
            setODriveStack(newStack);
            setODriveFolderId(crumb.id);
            setODriveNext(null);
            setODriveFiles([]);
            setSelectedODrive(new Set());
            loadOneDriveFiles('fresh', { folderId: crumb.id });
        },
        [oDriveStack, loadOneDriveFiles]
    );

    const toggleODriveFile = (id, opts = {}) => {
        if (opts.isFolder) return;
        setSelectedODrive((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runOneDriveImport = async () => {
        setLocalError(null);
        setImportLog(null);
        const itemIds = Array.from(selectedODrive);
        if (!ms?.connected) {
            setLocalError('Connect Microsoft 365 first.');
            return;
        }
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        if (itemIds.length === 0) {
            setLocalError('Select at least one file to import.');
            return;
        }
        setBusy('ms-od-import');
        try {
            const res = await importOneDriveFilesAPI({ projectId: selectedProjectId, itemIds });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Imported file',
                    content: d.content,
                    type: 'document',
                    source: 'oneDrive',
                    link: d.webViewLink || null
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(
                `Imported ${docs.length} OneDrive file(s). Embedded ${embedded} into the knowledge base.`
            );
            setSelectedODrive(new Set());
        } catch (e) {
            setLocalError(e?.message || 'OneDrive import failed');
        } finally {
            setBusy(null);
        }
    };

    const searchSpSites = async () => {
        if (!ms?.connected) return;
        setLocalError(null);
        setBusy('sp-search');
        try {
            const res = await searchSharePointSitesAPI({ q: spQuery });
            setSpSites(Array.isArray(res?.sites) ? res.sites : []);
            setSpSiteId('');
            setSpDrives([]);
            setSpDriveId('');
            setSpFiles([]);
            setSpNext(null);
        } catch (e) {
            setLocalError(e?.message || 'Site search failed');
        } finally {
            setBusy(null);
        }
    };

    const loadSpDrives = async (siteId) => {
        if (!siteId || !ms?.connected) return;
        setLocalError(null);
        setBusy('sp-drives');
        try {
            const res = await listSharePointDrivesAPI({ siteId });
            setSpDrives(Array.isArray(res?.drives) ? res.drives : []);
            setSpDriveId('');
            setSpFiles([]);
            setSpNext(null);
        } catch (e) {
            setLocalError(e?.message || 'Could not list document libraries');
        } finally {
            setBusy(null);
        }
    };

    const loadSpFiles = useCallback(
        async (mode) => {
            if (!ms?.connected || !spDriveId) {
                setLocalError('Pick a site and a library (drive) first.');
                return;
            }
            setLocalError(null);
            setBusy('sp-list');
            try {
                const res = await listGraphDriveItemsAPI({
                    driveId: spDriveId,
                    q: spListQ,
                    pageToken: mode === 'more' ? spNext : null,
                    pageSize: 30
                });
                const newFiles = Array.isArray(res?.files) ? res.files : [];
                setSpFiles((prev) => (mode === 'more' ? [...prev, ...newFiles] : newFiles));
                setSpNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not list SharePoint files');
            } finally {
                setBusy(null);
            }
        },
        [ms?.connected, spDriveId, spListQ, spNext]
    );

    const toggleSp = (id) => {
        setSelectedSp((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runSharePointImport = async () => {
        setLocalError(null);
        setImportLog(null);
        const itemIds = Array.from(selectedSp);
        if (!ms?.connected || !spDriveId) {
            setLocalError('Connect Microsoft and select a site library first.');
            return;
        }
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        if (itemIds.length === 0) {
            setLocalError('Select at least one file.');
            return;
        }
        setBusy('sp-import');
        try {
            const res = await importOneDriveFilesAPI({
                projectId: selectedProjectId,
                itemIds,
                driveId: spDriveId
            });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Imported file',
                    content: d.content,
                    type: 'document',
                    source: 'sharePoint',
                    link: d.webViewLink || null
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(
                `Imported ${docs.length} SharePoint file(s). Embedded ${embedded} into the knowledge base.`
            );
            setSelectedSp(new Set());
        } catch (e) {
            setLocalError(e?.message || 'SharePoint import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadOnNotebooks = async () => {
        if (!ms?.connected) return;
        setBusy('on-nb');
        setLocalError(null);
        try {
            const res = await listOneNoteNotebooksAPI();
            setOnNotebooks(Array.isArray(res?.notebooks) ? res.notebooks : []);
        } catch (e) {
            setLocalError(e?.message || 'Could not list OneNote notebooks');
        } finally {
            setBusy(null);
        }
    };

    const loadOnSections = async (notebookId) => {
        if (!notebookId) return;
        setBusy('on-sec');
        setLocalError(null);
        try {
            const res = await listOneNoteSectionsAPI({ notebookId });
            setOnSections(Array.isArray(res?.sections) ? res.sections : []);
            setOnSecId('');
            setOnPages([]);
            setOnPageNext(null);
        } catch (e) {
            setLocalError(e?.message || 'Could not list sections');
        } finally {
            setBusy(null);
        }
    };

    const loadOnPages = useCallback(
        async (mode) => {
            if (!onSecId) return;
            setBusy('on-pg');
            setLocalError(null);
            try {
                const res = await listOneNotePagesAPI({
                    sectionId: onSecId,
                    pageToken: mode === 'more' ? onPageNext : null,
                    pageSize: 30
                });
                const pgs = Array.isArray(res?.pages) ? res.pages : [];
                setOnPages((prev) => (mode === 'more' ? [...prev, ...pgs] : pgs));
                setOnPageNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not list OneNote pages');
            } finally {
                setBusy(null);
            }
        },
        [onSecId, onPageNext]
    );

    const toggleOnPage = (id) => {
        setSelectedOnPages((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runOneNoteImport = async () => {
        if (!ms?.connected) return;
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        const pageIds = Array.from(selectedOnPages);
        if (pageIds.length === 0) {
            setLocalError('Select at least one page.');
            return;
        }
        setBusy('on-imp');
        setImportLog(null);
        setLocalError(null);
        try {
            const res = await importOneNotePagesAPI({ projectId: selectedProjectId, pageIds });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'OneNote',
                    content: d.content,
                    type: 'document',
                    source: 'oneNote',
                    link: null
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(
                `Imported ${docs.length} OneNote page(s). Embedded ${embedded} into the knowledge base.`
            );
            setSelectedOnPages(new Set());
        } catch (e) {
            setLocalError(e?.message || 'OneNote import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadMsTeams = async () => {
        if (!ms?.connected) return;
        setBusy('tms-teams');
        setLocalError(null);
        try {
            const res = await listMicrosoftTeamsAPI();
            setMsTeams(Array.isArray(res?.teams) ? res.teams : []);
        } catch (e) {
            setLocalError(e?.message || 'Could not list teams');
        } finally {
            setBusy(null);
        }
    };

    const loadTcChannels = async (teamId) => {
        if (!teamId) return;
        setBusy('tms-ch');
        setLocalError(null);
        try {
            const res = await listMicrosoftTeamChannelsAPI({ teamId });
            setTcChannels(Array.isArray(res?.channels) ? res.channels : []);
            setTcChannelId('');
            setTcMsgs([]);
            setTcMsgNext(null);
        } catch (e) {
            setLocalError(e?.message || 'Could not list channels');
        } finally {
            setBusy(null);
        }
    };

    const loadTcMsgs = useCallback(
        async (mode) => {
            if (!tcTeamId || !tcChannelId) return;
            setBusy('tms-msg');
            setLocalError(null);
            try {
                const res = await listTeamChannelMessagesAPI({
                    teamId: tcTeamId,
                    channelId: tcChannelId,
                    pageToken: mode === 'more' ? tcMsgNext : null,
                    pageSize: 30
                });
                const m = Array.isArray(res?.messages) ? res.messages : [];
                setTcMsgs((prev) => (mode === 'more' ? [...prev, ...m] : m));
                setTcMsgNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not list channel messages');
            } finally {
                setBusy(null);
            }
        },
        [tcTeamId, tcChannelId, tcMsgNext]
    );

    const toggleTc = (id) => {
        setSelectedTc((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const importTcMessages = async () => {
        if (!selectedProjectId) {
            setLocalError('Choose a project.');
            return;
        }
        const messageIds = Array.from(selectedTc);
        if (!tcTeamId || !tcChannelId || messageIds.length === 0) {
            setLocalError('Pick a team, channel, and at least one message.');
            return;
        }
        setBusy('tms-imp');
        setLocalError(null);
        setImportLog(null);
        try {
            const res = await importTeamChannelMessagesAPI({
                projectId: selectedProjectId,
                teamId: tcTeamId,
                channelId: tcChannelId,
                messageIds
            });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Teams channel',
                    content: d.content,
                    type: 'document',
                    source: 'teamsChannel',
                    link: null
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(`Teams channel: embedded ${embedded} document(s) into the knowledge base.`);
            setSelectedTc(new Set());
        } catch (e) {
            setLocalError(e?.message || 'Teams channel import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadTChats = async () => {
        if (!ms?.connected) return;
        setBusy('tch-list');
        setLocalError(null);
        try {
            const res = await listMicrosoftChatsAPI();
            setMsChats(Array.isArray(res?.chats) ? res.chats : []);
        } catch (e) {
            setLocalError(e?.message || 'Could not list chats');
        } finally {
            setBusy(null);
        }
    };

    const loadTChatMsgs = useCallback(
        async (mode) => {
            if (!tChatId) return;
            setBusy('tch-msg');
            setLocalError(null);
            try {
                const res = await listChatMessagesAPI({
                    chatId: tChatId,
                    pageToken: mode === 'more' ? tChatNext : null,
                    pageSize: 30
                });
                const m = Array.isArray(res?.messages) ? res.messages : [];
                setTChatMsgs((prev) => (mode === 'more' ? [...prev, ...m] : m));
                setTChatNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not list chat messages');
            } finally {
                setBusy(null);
            }
        },
        [tChatId, tChatNext]
    );

    const toggleTChat = (id) => {
        setSelectedTChat((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const importTChatBundle = async () => {
        if (!selectedProjectId) {
            setLocalError('Choose a project.');
            return;
        }
        const messageIds = Array.from(selectedTChat);
        if (!tChatId || messageIds.length === 0) {
            setLocalError('Pick a chat and at least one message.');
            return;
        }
        setBusy('tch-imp');
        setLocalError(null);
        setImportLog(null);
        try {
            const res = await importChatMessagesAPI({
                projectId: selectedProjectId,
                chatId: tChatId,
                messageIds
            });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Teams chat',
                    content: d.content,
                    type: 'document',
                    source: 'teamsChat',
                    link: null
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(`Teams chat: embedded ${embedded} document(s) into the knowledge base.`);
            setSelectedTChat(new Set());
        } catch (e) {
            setLocalError(e?.message || 'Teams chat import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadGmail = useCallback(
        async (mode) => {
            if (!google?.connected) {
                setLocalError('Connect Google first.');
                return;
            }
            setLocalError(null);
            setBusy('gmail-list');
            try {
                const res = await listGmailMessagesAPI({
                    q: gmailSearchQ,
                    pageToken: mode === 'more' ? gmailNext : null,
                    pageSize: 20
                });
                const msgs = Array.isArray(res?.messages) ? res.messages : [];
                setGmailMessages((prev) => (mode === 'more' ? [...prev, ...msgs] : msgs));
                setGmailNext(res?.nextPageToken || null);
            } catch (e) {
                setLocalError(e?.message || 'Could not load Gmail messages');
            } finally {
                setBusy(null);
            }
        },
        [google?.connected, gmailSearchQ, gmailNext]
    );

    const toggleGmail = (id) => {
        setSelectedGmail((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runGmailImport = async () => {
        setLocalError(null);
        setImportLog(null);
        const messageIds = Array.from(selectedGmail);
        if (!google?.connected) {
            setLocalError('Connect Google first.');
            return;
        }
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        if (messageIds.length === 0) {
            setLocalError('Select at least one email to import.');
            return;
        }
        setBusy('gmail-import');
        try {
            const res = await importGmailMessagesAPI({ projectId: selectedProjectId, messageIds });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Imported email',
                    content: d.content,
                    type: 'document',
                    source: 'gmail'
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(`Imported ${docs.length} email(s). Embedded ${embedded} into the knowledge base.`);
            setSelectedGmail(new Set());
        } catch (e) {
            setLocalError(e?.message || 'Email import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadChatSpaces = async (mode) => {
        if (!google?.connected) {
            setLocalError('Connect Google first.');
            return;
        }
        setLocalError(null);
        setBusy('chat-spaces');
        try {
            const res = await listGoogleChatSpacesAPI({
                pageToken: mode === 'more' ? chatSpaceNext : null,
                pageSize: 50
            });
            const spaces = Array.isArray(res?.spaces) ? res.spaces : [];
            setChatSpaces((prev) => (mode === 'more' ? [...prev, ...spaces] : spaces));
            setChatSpaceNext(res?.nextPageToken || null);
        } catch (e) {
            setLocalError(e?.message || 'Could not load Chat spaces');
        } finally {
            setBusy(null);
        }
    };

    const loadChatMessages = async (mode) => {
        if (!google?.connected || !selectedChatSpace) {
            setLocalError('Connect Google and pick a Chat space.');
            return;
        }
        setLocalError(null);
        setBusy('chat-msg');
        try {
            const res = await listGoogleChatMessagesAPI({
                spaceName: selectedChatSpace,
                pageToken: mode === 'more' ? chatMsgNext : null,
                pageSize: 30
            });
            const msgs = Array.isArray(res?.messages) ? res.messages : [];
            setChatMessages((prev) => (mode === 'more' ? [...prev, ...msgs] : msgs));
            setChatMsgNext(res?.nextPageToken || null);
        } catch (e) {
            setLocalError(e?.message || 'Could not load Chat messages');
        } finally {
            setBusy(null);
        }
    };

    const toggleChatMsg = (name) => {
        setSelectedChatMsg((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const runChatImport = async () => {
        setLocalError(null);
        setImportLog(null);
        const names = Array.from(selectedChatMsg);
        if (!google?.connected) {
            setLocalError('Connect Google first.');
            return;
        }
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        if (names.length === 0) {
            setLocalError('Select at least one Chat message.');
            return;
        }
        setBusy('chat-import');
        try {
            const res = await importGoogleChatMessagesAPI({ projectId: selectedProjectId, messageNames: names });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Chat message',
                    content: d.content,
                    type: 'document',
                    source: 'googleChat'
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(`Imported ${docs.length} Chat message(s). Embedded ${embedded}.`);
            setSelectedChatMsg(new Set());
        } catch (e) {
            setLocalError(e?.message || 'Chat import failed');
        } finally {
            setBusy(null);
        }
    };

    const loadTaskListsOnce = async () => {
        if (!google?.connected) {
            setLocalError('Connect Google first.');
            return;
        }
        setLocalError(null);
        setBusy('task-lists');
        try {
            const res = await listGoogleTaskListsAPI();
            const lists = Array.isArray(res?.taskLists) ? res.taskLists : [];
            setTaskLists(lists);
            if (lists.length && !selectedTaskListId) {
                setSelectedTaskListId(lists[0].id);
                setSelectedTaskListTitle(lists[0].title || '');
            }
        } catch (e) {
            setLocalError(e?.message || 'Could not load task lists');
        } finally {
            setBusy(null);
        }
    };

    const loadGoogleTasksForList = async () => {
        if (!google?.connected || !selectedTaskListId) {
            setLocalError('Connect Google and pick a task list.');
            return;
        }
        setLocalError(null);
        setBusy('task-list');
        try {
            const res = await listGoogleTasksAPI({
                taskListId: selectedTaskListId,
                showCompleted: showCompletedTasks
            });
            const list = Array.isArray(res?.tasks) ? res.tasks : [];
            setGoogleTasks(list);
        } catch (e) {
            setLocalError(e?.message || 'Could not load tasks');
        } finally {
            setBusy(null);
        }
    };

    const toggleGoogleTask = (id) => {
        setSelectedGoogleTasks((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runGoogleTasksImport = async () => {
        setLocalError(null);
        setImportLog(null);
        const taskIds = Array.from(selectedGoogleTasks);
        if (!google?.connected) {
            setLocalError('Connect Google first.');
            return;
        }
        if (!selectedProjectId) {
            setLocalError('Choose a project for this import.');
            return;
        }
        if (!selectedTaskListId) {
            setLocalError('Pick a task list.');
            return;
        }
        if (taskIds.length === 0) {
            setLocalError('Select at least one task.');
            return;
        }
        setBusy('task-import');
        try {
            const res = await importGoogleTasksAPI({
                projectId: selectedProjectId,
                taskListId: selectedTaskListId,
                taskIds,
                taskListTitle: selectedTaskListTitle
            });
            const docs = Array.isArray(res?.documents) ? res.documents : [];
            let embedded = 0;
            for (const d of docs) {
                if (!d?.documentId || !d?.content) continue;
                const emb = {
                    id: d.documentId,
                    title: d.title || 'Task',
                    content: d.content,
                    type: 'document',
                    source: 'googleTasks'
                };
                const r = await ragService.embedDocument(emb, user.uid, selectedProjectId);
                if (r?.success) embedded += 1;
            }
            setImportLog(`Imported ${docs.length} task(s). Embedded ${embedded}.`);
            setSelectedGoogleTasks(new Set());
        } catch (e) {
            setLocalError(e?.message || 'Task import failed');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="max-w-3xl space-y-8">
            <section className="rounded-2xl border border-gray-200/80 bg-white/80 backdrop-blur shadow-sm overflow-hidden">
                <button
                    type="button"
                    onClick={() => setImportHistoryOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50/80 transition-colors"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 rounded-xl bg-slate-100 text-slate-600 flex-shrink-0">
                            <History className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-slate-900">Import history</h3>
                            <p className="text-sm text-slate-500 truncate">
                                {importHistory.length === 0
                                    ? 'No imports yet'
                                    : `${importHistory.length} item${importHistory.length === 1 ? '' : 's'} from Drive, Gmail, Chat, Tasks, or OneDrive`}
                            </p>
                        </div>
                    </div>
                    {importHistoryOpen ? (
                        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                </button>
                {importHistoryOpen && (
                    <div className="px-5 pb-5 border-t border-slate-100">
                        <p className="text-sm text-slate-600 py-3">
                            Embedded into your knowledge base for AI chat. Same rows appear in Documents for your
                            project.
                        </p>
                        {importHistory.length === 0 ? (
                            <p className="text-sm text-slate-500">No integration imports yet.</p>
                        ) : (
                            <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-left text-slate-600">
                                        <tr>
                                            <th className="px-3 py-2 font-medium">Source</th>
                                            <th className="px-3 py-2 font-medium">Title</th>
                                            <th className="px-3 py-2 font-medium">Project</th>
                                            <th className="px-3 py-2 font-medium">When</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {importHistory.map((row) => (
                                            <tr key={row.id} className="hover:bg-slate-50/80">
                                                <td className="px-3 py-2 text-slate-700">
                                                    <ImportSourceBadge provider={row.source?.provider} />
                                                </td>
                                                <td className="px-3 py-2 text-slate-900 line-clamp-2">
                                                    {row.fileName || row.subject || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-slate-600">
                                                    {row.projectId ? projectNameById[row.projectId] || row.projectId : '—'}
                                                </td>
                                                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                                                    {formatDocDate(row)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {localError && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
                    <p className="font-medium">Something went wrong</p>
                    <p className="mt-1">{localError}</p>
                    {errorLooksGmailRelated ? (
                        <p className="mt-2 text-red-600/80">{GMAIL_ERR_HINT}</p>
                    ) : (
                        <p className="mt-2 text-red-600/80">{OAUTH_ERR_HINT}</p>
                    )}
                </div>
            )}

            <div className="space-y-4">
                <section className="rounded-2xl border border-gray-200/80 bg-white/80 backdrop-blur p-6 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center justify-center p-1.5 flex-shrink-0">
                                <img
                                    src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png"
                                    alt=""
                                    className="w-8 h-8"
                                />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900">Google</h3>
                                <p className="text-sm text-slate-600 mt-0.5">
                                    Drive, Gmail, Chat, and Tasks — one connection for imports into your projects.
                                </p>
                                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                                    {google?.connected ? (
                                        <>
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                            Connected{google?.email ? ` as ${google.email}` : ''}
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-3.5 h-3.5 text-slate-400" />
                                            Not connected
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                            {google?.connected ? (
                                <button
                                    type="button"
                                    onClick={() => onDisconnect('google')}
                                    disabled={!!busy}
                                    className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                    {busy === 'dis-google' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disconnect'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => goOAuth('google')}
                                    disabled={!!busy}
                                    className="px-4 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    {busy === 'google' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Link2 className="w-4 h-4" />
                                            Connect
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {google?.connected && (
                        <div className="mt-6 border-t border-slate-100 pt-5 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-slate-600">Import into</span>
                                    <select
                                        value={selectedProjectId}
                                        onChange={(e) => setSelectedProjectId(e.target.value)}
                                        className="text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 min-w-[10rem]"
                                    >
                                        <option value="">Select a project…</option>
                                        {projectOptions.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {importLog && (
                                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                                    {importLog}
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:flex sm:flex-wrap p-1 bg-slate-100/90 rounded-xl gap-0.5">
                                <GoogleTabButton
                                    label="Drive"
                                    src={GOOGLE_FAV.drive}
                                    IconFallback={Folder}
                                    active={googleSubTab === 'drive'}
                                    onClick={() => setGoogleSubTab('drive')}
                                />
                                <GoogleTabButton
                                    label="Mail"
                                    src={GOOGLE_FAV.mail}
                                    IconFallback={Mail}
                                    active={googleSubTab === 'mail'}
                                    onClick={() => setGoogleSubTab('mail')}
                                />
                                <GoogleTabButton
                                    label="Chat"
                                    src={GOOGLE_FAV.chat}
                                    IconFallback={MessageSquare}
                                    active={googleSubTab === 'chat'}
                                    onClick={() => setGoogleSubTab('chat')}
                                />
                                <GoogleTabButton
                                    label="Tasks"
                                    src={GOOGLE_FAV.tasks}
                                    IconFallback={ListTodo}
                                    active={googleSubTab === 'tasks'}
                                    onClick={() => setGoogleSubTab('tasks')}
                                />
                            </div>

                            {googleSubTab === 'drive' && (
                            <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                                    <Search className="w-4 h-4 text-slate-500" />
                                    <input
                                        value={driveQuery}
                                        onChange={(e) => setDriveQuery(e.target.value)}
                                        placeholder="Search Drive (Docs, Sheets, Slides, PDF, CSV, Word, Excel, PPT, text…)"
                                        className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => loadDriveFiles('fresh')}
                                    disabled={busy === 'drive-list' || busy === 'drive-import'}
                                    className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                >
                                    {busy === 'drive-list' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Load files'
                                    )}
                                </button>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={runImport}
                                    disabled={busy === 'drive-list' || busy === 'drive-import'}
                                    className="px-4 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                >
                                    {busy === 'drive-import' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            Import selected
                                        </>
                                    )}
                                </button>
                            </div>

                            {driveFiles.length > 0 ? (
                                <div className="rounded-xl border border-slate-200 overflow-hidden">
                                    <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                                        {driveFiles.map((f) => (
                                            <label
                                                key={f.id}
                                                className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDrive.has(f.id)}
                                                    onChange={() => toggleDriveFile(f.id)}
                                                    className="mt-1"
                                                />
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-slate-900 truncate">
                                                        {f.name}
                                                    </div>
                                                    <div className="text-xs text-slate-500 mt-0.5">
                                                        {f.mimeType}
                                                        {f.modifiedTime ? ` • ${new Date(f.modifiedTime).toLocaleString()}` : ''}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    {driveNext && (
                                        <div className="p-3 bg-white border-t border-slate-200 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => loadDriveFiles('more')}
                                                disabled={busy === 'drive-list' || busy === 'drive-import'}
                                                className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                            >
                                                Load more
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500">
                                    Load files to select what to import. Supports Google Docs/Sheets/Slides (exported as
                                    text/CSV), PDF, CSV, Word (.docx), Excel (.xlsx), PowerPoint (.pptx), and plain text.
                                </p>
                            )}
                            </div>
                            )}

                            {googleSubTab === 'mail' && (
                            <div className="space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900">Gmail (one-time import)</h4>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Uses the same Google sign-in as Drive. First time with Gmail: enable the Gmail API
                                        in Google Cloud (same project as your OAuth client), then disconnect and
                                        reconnect here so Google can grant the new permission.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <label className="block text-sm">
                                        <span className="text-slate-600">Time window</span>
                                        <select
                                            value={gmailTimePreset}
                                            onChange={(e) => setGmailTimePreset(e.target.value)}
                                            className="mt-1 w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2"
                                        >
                                            <option value="7d">Last 7 days</option>
                                            <option value="30d">Last 30 days</option>
                                            <option value="90d">Last 90 days</option>
                                            <option value="180d">Last 6 months</option>
                                            <option value="365d">Last year</option>
                                            <option value="all">Any time (entire mailbox — can be slow)</option>
                                        </select>
                                    </label>
                                    <label className="block text-sm">
                                        <span className="text-slate-600">From (optional)</span>
                                        <input
                                            value={gmailFrom}
                                            onChange={(e) => setGmailFrom(e.target.value)}
                                            placeholder="name@company.com, other@…"
                                            className="mt-1 w-full text-sm rounded-xl border border-slate-200 px-3 py-2"
                                        />
                                    </label>
                                    <label className="block text-sm sm:col-span-2">
                                        <span className="text-slate-600">Subject contains (optional)</span>
                                        <input
                                            value={gmailSubject}
                                            onChange={(e) => setGmailSubject(e.target.value)}
                                            placeholder="e.g. invoice, Q1 planning"
                                            className="mt-1 w-full text-sm rounded-xl border border-slate-200 px-3 py-2"
                                        />
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                                        <input
                                            type="checkbox"
                                            checked={gmailHasAttachment}
                                            onChange={(e) => setGmailHasAttachment(e.target.checked)}
                                            className="rounded border-slate-300"
                                        />
                                        Only messages with attachments
                                    </label>
                                </div>

                                <details className="text-sm">
                                    <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                                        Advanced: raw Gmail search (overrides everything above)
                                    </summary>
                                    <textarea
                                        value={gmailAdvanced}
                                        onChange={(e) => setGmailAdvanced(e.target.value)}
                                        rows={2}
                                        placeholder="Example: from:boss@co.com newer_than:7d has:attachment"
                                        className="mt-2 w-full text-sm rounded-xl border border-slate-200 px-3 py-2 font-mono"
                                    />
                                </details>

                                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                    <div className="flex-1 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-2">
                                        <div className="text-xs text-slate-500">Gmail will search</div>
                                        <div className="text-xs font-mono text-slate-800 break-all mt-0.5">
                                            {gmailSearchQ || '(empty)'}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => loadGmail('fresh')}
                                        disabled={busy === 'gmail-list' || busy === 'gmail-import'}
                                        className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-2 shrink-0"
                                    >
                                        {busy === 'gmail-list' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Search className="w-4 h-4" />
                                                Load emails
                                            </>
                                        )}
                                    </button>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={runGmailImport}
                                        disabled={busy === 'gmail-list' || busy === 'gmail-import'}
                                        className="px-4 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                    >
                                        {busy === 'gmail-import' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                Import selected emails
                                            </>
                                        )}
                                    </button>
                                </div>

                                {gmailMessages.length > 0 ? (
                                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                                        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                                            {gmailMessages.map((m) => (
                                                <label
                                                    key={m.id}
                                                    className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedGmail.has(m.id)}
                                                        onChange={() => toggleGmail(m.id)}
                                                        className="mt-1"
                                                    />
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-slate-900 truncate">
                                                            {m.subject || '(no subject)'}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                                                            {m.from || 'Unknown sender'}
                                                            {m.date ? ` • ${m.date}` : ''}
                                                        </div>
                                                        {m.snippet && (
                                                            <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                                                                {m.snippet}
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                        {gmailNext && (
                                            <div className="p-3 bg-white border-t border-slate-200 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => loadGmail('more')}
                                                    disabled={busy === 'gmail-list' || busy === 'gmail-import'}
                                                    className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                                >
                                                    Load more
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-500">
                                        Set filters above, then load emails. Use Advanced only if you need the full Gmail
                                        search language.
                                    </p>
                                )}
                            </div>
                            )}

                            {googleSubTab === 'chat' && (
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-11 h-11 rounded-xl bg-white border border-slate-200/80 shadow-sm flex items-center justify-center p-1.5 flex-shrink-0">
                                        <img
                                            src={GOOGLE_CHAT_LOGO_48}
                                            alt=""
                                            className="w-8 h-8 object-contain"
                                        />
                                    </div>
                                    <div className="min-w-0 pt-0.5">
                                        <h4 className="text-sm font-semibold text-slate-900">Google Chat</h4>
                                        <p className="text-xs text-slate-500 mt-1">
                                            One-time import into your project. Enable the Chat API in Google Cloud and add
                                            Chat scopes on the consent screen, then disconnect and reconnect Google. Imports
                                            message text for AI search.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => loadChatSpaces('fresh')}
                                        disabled={busy === 'chat-spaces' || busy === 'chat-msg' || busy === 'chat-import'}
                                        className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        {busy === 'chat-spaces' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            'Load spaces'
                                        )}
                                    </button>
                                    {chatSpaceNext && (
                                        <button
                                            type="button"
                                            onClick={() => loadChatSpaces('more')}
                                            disabled={busy === 'chat-spaces'}
                                            className="px-3 py-2 text-sm rounded-xl border border-slate-200"
                                        >
                                            More spaces
                                        </button>
                                    )}
                                </div>
                                {chatSpaces.length > 0 && (
                                    <label className="block text-sm">
                                        <span className="text-slate-600">Space</span>
                                        <select
                                            value={selectedChatSpace}
                                            onChange={(e) => {
                                                setSelectedChatSpace(e.target.value);
                                                setChatMessages([]);
                                                setChatMsgNext(null);
                                                setSelectedChatMsg(new Set());
                                            }}
                                            className="mt-1 w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2"
                                        >
                                            <option value="">Select a space…</option>
                                            {chatSpaces.map((s) => (
                                                <option key={s.name} value={s.name}>
                                                    {s.displayName || s.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => loadChatMessages('fresh')}
                                        disabled={!selectedChatSpace || busy === 'chat-msg' || busy === 'chat-import'}
                                        className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        {busy === 'chat-msg' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            'Load messages'
                                        )}
                                    </button>
                                    {chatMsgNext && (
                                        <button
                                            type="button"
                                            onClick={() => loadChatMessages('more')}
                                            disabled={busy === 'chat-msg'}
                                            className="px-3 py-2 text-sm rounded-xl border border-slate-200"
                                        >
                                            More messages
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={runChatImport}
                                        disabled={busy === 'chat-import' || selectedChatMsg.size === 0}
                                        className="px-3 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
                                    >
                                        {busy === 'chat-import' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            'Import selected messages'
                                        )}
                                    </button>
                                </div>
                                {chatMessages.length > 0 ? (
                                    <div className="rounded-xl border border-slate-200 max-h-56 overflow-y-auto divide-y divide-slate-100">
                                        {chatMessages.map((m) => (
                                            <label
                                                key={m.name}
                                                className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedChatMsg.has(m.name)}
                                                    onChange={() => toggleChatMsg(m.name)}
                                                    className="mt-1"
                                                />
                                                <div className="min-w-0 text-sm">
                                                    <div className="text-slate-900 line-clamp-2">
                                                        {m.text || '(no text)'}
                                                    </div>
                                                    {m.createTime && (
                                                        <div className="text-xs text-slate-500 mt-0.5">{m.createTime}</div>
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-500">
                                        Load spaces, pick a space, then load messages to select what to import.
                                    </p>
                                )}
                            </div>
                            )}

                            {googleSubTab === 'tasks' && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <ListTodo className="w-4 h-4 text-slate-600" />
                                    <h4 className="text-sm font-semibold text-slate-900">Google Tasks (one-time import)</h4>
                                </div>
                                <p className="text-xs text-slate-500">
                                    Enable the Tasks API in Google Cloud, add the Tasks read-only scope, then reconnect
                                    Google. Imports task title and notes as text for the knowledge base.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={loadTaskListsOnce}
                                        disabled={busy === 'task-lists'}
                                        className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200"
                                    >
                                        {busy === 'task-lists' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            'Load task lists'
                                        )}
                                    </button>
                                </div>
                                {taskLists.length > 0 && (
                                    <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
                                        <label className="block text-sm flex-1 min-w-[200px]">
                                            <span className="text-slate-600">List</span>
                                            <select
                                                value={selectedTaskListId}
                                                onChange={(e) => {
                                                    const id = e.target.value;
                                                    setSelectedTaskListId(id);
                                                    const t = taskLists.find((l) => l.id === id);
                                                    setSelectedTaskListTitle(t?.title || '');
                                                    setGoogleTasks([]);
                                                    setSelectedGoogleTasks(new Set());
                                                }}
                                                className="mt-1 w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2"
                                            >
                                                {taskLists.map((l) => (
                                                    <option key={l.id} value={l.id}>
                                                        {l.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={showCompletedTasks}
                                                onChange={(e) => setShowCompletedTasks(e.target.checked)}
                                            />
                                            Include completed
                                        </label>
                                        <button
                                            type="button"
                                            onClick={loadGoogleTasksForList}
                                            disabled={busy === 'task-list' || !selectedTaskListId}
                                            className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200"
                                        >
                                            {busy === 'task-list' ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                'Load tasks'
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={runGoogleTasksImport}
                                            disabled={busy === 'task-import' || selectedGoogleTasks.size === 0}
                                            className="px-3 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white"
                                        >
                                            {busy === 'task-import' ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                'Import selected'
                                            )}
                                        </button>
                                    </div>
                                )}
                                {googleTasks.length > 0 ? (
                                    <div className="rounded-xl border border-slate-200 max-h-56 overflow-y-auto divide-y divide-slate-100">
                                        {googleTasks.map((t) => (
                                            <label
                                                key={t.id}
                                                className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedGoogleTasks.has(t.id)}
                                                    onChange={() => toggleGoogleTask(t.id)}
                                                    className="mt-1"
                                                />
                                                <div className="min-w-0 text-sm">
                                                    <div className="font-medium text-slate-900">{t.title}</div>
                                                    <div className="text-xs text-slate-500">
                                                        {t.status}
                                                        {t.due ? ` • due ${t.due}` : ''}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                ) : taskLists.length > 0 ? (
                                    <p className="text-sm text-slate-500">Pick a list and load tasks.</p>
                                ) : null}
                            </div>
                            )}
                </div>
                    )}
                </section>

                <section className="rounded-2xl border border-gray-200/80 bg-white/80 backdrop-blur p-6 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center justify-center p-1.5 flex-shrink-0">
                                <img src={MS365_FAV.suite} alt="" className="w-8 h-8 object-contain" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900">Microsoft 365</h3>
                                <p className="text-sm text-slate-600 mt-0.5">
                                    OneDrive file import (PDF, Word, Excel, PowerPoint, CSV, text) into your projects —
                                    read-only via Microsoft Graph.
                                </p>
                                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                                    {ms?.connected ? (
                                        <>
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                            Connected{ms?.email ? ` as ${ms.email}` : ''}
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-3.5 h-3.5 text-slate-400" />
                                            Not connected
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                            {ms?.connected ? (
                                <button
                                    type="button"
                                    onClick={() => onDisconnect('microsoft')}
                                    disabled={!!busy}
                                    className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                    {busy === 'dis-microsoft' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Disconnect'
                                    )}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => goOAuth('microsoft')}
                                    disabled={!!busy}
                                    className="px-4 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    {busy === 'microsoft' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Link2 className="w-4 h-4" />
                                            Connect
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {ms?.connected && (
                        <div className="mt-6 border-t border-slate-100 pt-5 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-slate-600">Import into</span>
                                    <select
                                        value={selectedProjectId}
                                        onChange={(e) => setSelectedProjectId(e.target.value)}
                                        className="text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 min-w-[10rem]"
                                    >
                                        <option value="">Select a project…</option>
                                        {projectOptions.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {importLog && (
                                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                                    {importLog}
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:flex sm:flex-wrap p-1 bg-slate-100/90 rounded-xl gap-0.5">
                                <GoogleTabButton
                                    label="OneDrive"
                                    src={MS365_FAV.onedrive}
                                    IconFallback={Folder}
                                    active={msSubTab === 'onedrive'}
                                    onClick={() => setMsSubTab('onedrive')}
                                />
                                <GoogleTabButton
                                    label="SharePoint"
                                    src={MS365_FAV.sharepoint}
                                    IconFallback={Folder}
                                    active={msSubTab === 'sharepoint'}
                                    onClick={() => setMsSubTab('sharepoint')}
                                />
                                <GoogleTabButton
                                    label="OneNote"
                                    src={MS365_FAV.onenote}
                                    IconFallback={ListTodo}
                                    active={msSubTab === 'onenote'}
                                    onClick={() => setMsSubTab('onenote')}
                                />
                                <GoogleTabButton
                                    label="Teams"
                                    src={MS365_FAV.teams}
                                    IconFallback={MessageSquare}
                                    active={msSubTab === 'teams'}
                                    onClick={() => setMsSubTab('teams')}
                                />
                            </div>

                            {msSubTab === 'onedrive' && (
                                <div className="space-y-4">
                                    <p className="text-xs text-slate-600">
                                        Lists <strong>all files</strong> in the current folder (open folders below).
                                        Embedding supports PDF, Word, Excel, PowerPoint, CSV/TSV, text, RTF, legacy
                                        Office, and common OpenDocument types; other files appear so you can see
                                        what is in OneDrive—open them in Microsoft 365 to work with them.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-1 text-xs text-slate-600">
                                        {oDriveStack.map((c, i) => (
                                            <span key={`${c.id ?? 'root'}-${i}`} className="inline-flex items-center gap-1">
                                                {i > 0 && (
                                                    <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
                                                )}
                                                <button
                                                    type="button"
                                                    className="hover:underline font-medium text-slate-800 disabled:text-slate-500 disabled:no-underline"
                                                    onClick={() => goODriveCrumb(i)}
                                                    disabled={i === oDriveStack.length - 1}
                                                >
                                                    {c.name}
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                        <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                                            <Search className="w-4 h-4 text-slate-500" />
                                            <input
                                                value={oDriveQuery}
                                                onChange={(e) => setODriveQuery(e.target.value)}
                                                placeholder="Search OneDrive by name (optional)"
                                                className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => loadOneDriveFiles('fresh')}
                                            disabled={busy === 'ms-od-list' || busy === 'ms-od-import'}
                                            className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                        >
                                            {busy === 'ms-od-list' ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                'Load files'
                                            )}
                                        </button>
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={runOneDriveImport}
                                            disabled={busy === 'ms-od-list' || busy === 'ms-od-import'}
                                            className="px-4 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                        >
                                            {busy === 'ms-od-import' ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Download className="w-4 h-4" />
                                                    Import selected
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {oDriveFiles.length > 0 ? (
                                        <div className="rounded-xl border border-slate-200 overflow-hidden">
                                            <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                                                {oDriveFiles.map((f) =>
                                                    f.isFolder ? (
                                                        <div
                                                            key={f.id}
                                                            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                                                        >
                                                            <Folder className="w-4 h-4 text-amber-700 shrink-0" />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="text-sm font-medium text-slate-900 truncate">
                                                                    {f.name}
                                                                </div>
                                                                <div className="text-xs text-slate-500">Folder</div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => openODriveFolder(f)}
                                                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-white shrink-0"
                                                            >
                                                                Open
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label
                                                            key={f.id}
                                                            className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 ${
                                                                f.importSupported === false
                                                                    ? 'cursor-default opacity-90'
                                                                    : 'cursor-pointer'
                                                            }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedODrive.has(f.id)}
                                                                onChange={() =>
                                                                    toggleODriveFile(f.id, {
                                                                        isFolder: !!f.isFolder
                                                                    })
                                                                }
                                                                disabled={f.importSupported === false}
                                                                title={
                                                                    f.importSupported === false
                                                                        ? 'Text extraction / embedding is not enabled for this type yet'
                                                                        : undefined
                                                                }
                                                                className="mt-1"
                                                            />
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium text-slate-900 truncate">
                                                                    {f.name}
                                                                </div>
                                                                <div className="text-xs text-slate-500 mt-0.5">
                                                                    {f.mimeType}
                                                                    {f.importSupported === false && (
                                                                        <span className="text-amber-700">
                                                                            {' '}
                                                                            · not imported into KB
                                                                        </span>
                                                                    )}
                                                                    {f.modifiedTime
                                                                        ? ` • ${new Date(f.modifiedTime).toLocaleString()}`
                                                                        : ''}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    )
                                                )}
                                            </div>
                                            {oDriveNext && (
                                                <div className="p-3 bg-white border-t border-slate-200 flex justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => loadOneDriveFiles('more')}
                                                        disabled={busy === 'ms-od-list' || busy === 'ms-od-import'}
                                                        className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                                    >
                                                        Load more
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-500">
                                            Click Load files to list everything in this folder (use Open on folders to
                                            go deeper). Import adds embedded text for supported document types.
                                        </p>
                                    )}
                                </div>
                            )}

                            {msSubTab === 'sharepoint' && (
                                <div className="space-y-4 text-sm">
                                    <p className="text-slate-600">
                                        Search a site, pick a document library, then list Word / Excel / PowerPoint /
                                        PDF and other supported files.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            value={spQuery}
                                            onChange={(e) => setSpQuery(e.target.value)}
                                            placeholder="Search SharePoint sites"
                                            className="flex-1 rounded-xl border border-slate-200 px-3 py-2"
                                        />
                                        <button
                                            type="button"
                                            onClick={searchSpSites}
                                            className="px-4 py-2 rounded-xl border border-slate-200"
                                            disabled={!!busy}
                                        >
                                            Search sites
                                        </button>
                                    </div>
                                    {spSites.length > 0 && (
                                        <label className="block">
                                            <span className="text-slate-600">Site</span>
                                            <select
                                                value={spSiteId}
                                                onChange={async (e) => {
                                                    const id = e.target.value;
                                                    setSpSiteId(id);
                                                    setSpDrives([]);
                                                    setSpDriveId('');
                                                    setSpFiles([]);
                                                    if (id) await loadSpDrives(id);
                                                }}
                                                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                                            >
                                                <option value="">Select site…</option>
                                                {spSites.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.displayName}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    )}
                                    {spDrives.length > 0 && (
                                        <label className="block">
                                            <span className="text-slate-600">Library (drive)</span>
                                            <select
                                                value={spDriveId}
                                                onChange={(e) => {
                                                    setSpDriveId(e.target.value);
                                                    setSpFiles([]);
                                                    setSpNext(null);
                                                }}
                                                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                                            >
                                                <option value="">Select library…</option>
                                                {spDrives.map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    )}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            value={spListQ}
                                            onChange={(e) => setSpListQ(e.target.value)}
                                            placeholder="Filter by file name in library (optional)"
                                            className="flex-1 rounded-xl border border-slate-200 px-3 py-2"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => loadSpFiles('fresh')}
                                            disabled={!spDriveId || busy === 'sp-list' || busy === 'sp-import'}
                                            className="px-4 py-2 rounded-xl border border-slate-200"
                                        >
                                            Load files
                                        </button>
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={runSharePointImport}
                                            disabled={busy === 'sp-list' || busy === 'sp-import' || !spDriveId}
                                            className="px-4 py-2 text-sm font-medium rounded-xl bg-slate-800 text-white"
                                        >
                                            {busy === 'sp-import' ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Download className="w-4 h-4 inline mr-1" />
                                                    Import selected
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {spFiles.length > 0 ? (
                                        <div className="rounded-xl border border-slate-200 max-h-72 overflow-y-auto divide-y">
                                            {spFiles.map((f) => (
                                                <label
                                                    key={f.id}
                                                    className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSp.has(f.id)}
                                                        onChange={() => toggleSp(f.id)}
                                                    />
                                                    <div className="min-w-0 text-sm">
                                                        <div className="font-medium truncate">{f.name}</div>
                                                        <div className="text-xs text-slate-500">{f.mimeType}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    ) : spDriveId ? (
                                        <p className="text-slate-500">Load files, then select items to import.</p>
                                    ) : null}
                                    {spNext && (
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => loadSpFiles('more')}
                                                className="px-3 py-2 text-sm border rounded-xl"
                                            >
                                                Load more
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {msSubTab === 'onenote' && (
                                <div className="space-y-3 text-sm">
                                    <p className="text-slate-600">
                                        Notebooks in your account: pick a section, list pages, then import page HTML as
                                        text.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={loadOnNotebooks}
                                        className="px-3 py-2 border rounded-xl"
                                        disabled={!!busy}
                                    >
                                        Load notebooks
                                    </button>
                                    {onNotebooks.length > 0 && (
                                        <div className="space-y-2">
                                            <label className="block">
                                                <span className="text-slate-600">Notebook</span>
                                                <select
                                                    value={onNbId}
                                                    onChange={async (e) => {
                                                        const v = e.target.value;
                                                        setOnNbId(v);
                                                        if (v) await loadOnSections(v);
                                                    }}
                                                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                                                >
                                                    <option value="">Select…</option>
                                                    {onNotebooks.map((n) => (
                                                        <option key={n.id} value={n.id}>
                                                            {n.displayName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            {onSections.length > 0 && (
                                                <label className="block">
                                                    <span className="text-slate-600">Section</span>
                                                    <select
                                                        value={onSecId}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setOnSecId(v);
                                                            setOnPages([]);
                                                            setOnPageNext(null);
                                                            setSelectedOnPages(new Set());
                                                        }}
                                                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                                                    >
                                                        <option value="">Select…</option>
                                                        {onSections.map((s) => (
                                                            <option key={s.id} value={s.id}>
                                                                {s.displayName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                            {onSecId && (
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => loadOnPages('fresh')}
                                                        className="px-3 py-2 border rounded-xl"
                                                    >
                                                        List pages
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {onPages.length > 0 && (
                                        <div className="rounded-xl border max-h-56 overflow-y-auto divide-y">
                                            {onPages.map((p) => (
                                                <label
                                                    key={p.id}
                                                    className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedOnPages.has(p.id)}
                                                        onChange={() => toggleOnPage(p.id)}
                                                    />
                                                    <span className="text-sm">{p.title}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    {onPageNext && (
                                        <button
                                            type="button"
                                            onClick={() => loadOnPages('more')}
                                            className="text-sm text-slate-600"
                                        >
                                            More pages
                                        </button>
                                    )}
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={runOneNoteImport}
                                            className="px-4 py-2 bg-slate-800 text-white rounded-xl"
                                            disabled={!!busy}
                                        >
                                            Import selected pages
                                        </button>
                                    </div>
                                </div>
                            )}

                            {msSubTab === 'teams' && (
                                <div className="space-y-4 text-sm">
                                    <div className="inline-flex p-0.5 bg-slate-100 rounded-lg">
                                        <button
                                            type="button"
                                            onClick={() => setTeamsView('channels')}
                                            className={`px-3 py-1.5 rounded-md text-sm ${
                                                teamsView === 'channels'
                                                    ? 'bg-white shadow text-slate-900'
                                                    : 'text-slate-600'
                                            }`}
                                        >
                                            Team channels
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setTeamsView('chats')}
                                            className={`px-3 py-1.5 rounded-md text-sm ${
                                                teamsView === 'chats' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
                                            }`}
                                        >
                                            Chats
                                        </button>
                                    </div>

                                    {teamsView === 'channels' && (
                                        <div className="space-y-3">
                                            <p className="text-slate-600">
                                                Requires Teams permissions on your app registration. Some tenants need
                                                admin approval.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={loadMsTeams}
                                                className="px-3 py-2 border rounded-xl"
                                            >
                                                Load my teams
                                            </button>
                                            {msTeams.length > 0 && (
                                                <label className="block">
                                                    <span className="text-slate-600">Team</span>
                                                    <select
                                                        value={tcTeamId}
                                                        onChange={async (e) => {
                                                            const t = e.target.value;
                                                            setTcTeamId(t);
                                                            if (t) await loadTcChannels(t);
                                                        }}
                                                        className="mt-1 w-full rounded-xl border px-3 py-2"
                                                    >
                                                        <option value="">Select…</option>
                                                        {msTeams.map((t) => (
                                                            <option key={t.id} value={t.id}>
                                                                {t.displayName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                            {tcChannels.length > 0 && (
                                                <label className="block">
                                                    <span className="text-slate-600">Channel</span>
                                                    <select
                                                        value={tcChannelId}
                                                        onChange={(e) => {
                                                            setTcChannelId(e.target.value);
                                                            setTcMsgs([]);
                                                            setTcMsgNext(null);
                                                        }}
                                                        className="mt-1 w-full rounded-xl border px-3 py-2"
                                                    >
                                                        <option value="">Select…</option>
                                                        {tcChannels.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                                {c.displayName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                            {tcChannelId && (
                                                <button
                                                    type="button"
                                                    onClick={() => loadTcMsgs('fresh')}
                                                    className="px-3 py-2 border rounded-xl"
                                                >
                                                    Load messages
                                                </button>
                                            )}
                                            {tcMsgs.length > 0 && (
                                                <div className="border rounded-xl max-h-64 overflow-y-auto divide-y text-xs">
                                                    {tcMsgs.map((m) => (
                                                        <label
                                                            key={m.id}
                                                            className="flex gap-2 p-2 cursor-pointer hover:bg-slate-50"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedTc.has(m.id)}
                                                                onChange={() => toggleTc(m.id)}
                                                            />
                                                            <div>
                                                                <div className="font-medium text-slate-800">
                                                                    {m.from}
                                                                </div>
                                                                <div className="text-slate-500 line-clamp-3">
                                                                    {m.preview}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                            {tcMsgNext && (
                                                <button
                                                    type="button"
                                                    onClick={() => loadTcMsgs('more')}
                                                    className="text-sm"
                                                >
                                                    More messages
                                                </button>
                                            )}
                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={importTcMessages}
                                                    className="px-4 py-2 bg-slate-800 text-white rounded-xl"
                                                >
                                                    Import selected (one document)
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {teamsView === 'chats' && (
                                        <div className="space-y-3">
                                            <button
                                                type="button"
                                                onClick={loadTChats}
                                                className="px-3 py-2 border rounded-xl"
                                            >
                                                Load my chats
                                            </button>
                                            {msChats.length > 0 && (
                                                <label className="block">
                                                    <span className="text-slate-600">Chat</span>
                                                    <select
                                                        value={tChatId}
                                                        onChange={(e) => {
                                                            setTChatId(e.target.value);
                                                            setTChatMsgs([]);
                                                            setTChatNext(null);
                                                        }}
                                                        className="mt-1 w-full rounded-xl border px-3 py-2"
                                                    >
                                                        <option value="">Select…</option>
                                                        {msChats.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                                {c.topic}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                            {tChatId && (
                                                <button
                                                    type="button"
                                                    onClick={() => loadTChatMsgs('fresh')}
                                                    className="px-3 py-2 border rounded-xl"
                                                >
                                                    Load messages
                                                </button>
                                            )}
                                            {tChatMsgs.length > 0 && (
                                                <div className="border rounded-xl max-h-64 overflow-y-auto divide-y text-xs">
                                                    {tChatMsgs.map((m) => (
                                                        <label
                                                            key={m.id}
                                                            className="flex gap-2 p-2 cursor-pointer hover:bg-slate-50"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedTChat.has(m.id)}
                                                                onChange={() => toggleTChat(m.id)}
                                                            />
                                                            <div>
                                                                <div className="font-medium">{m.from}</div>
                                                                <div className="line-clamp-2 text-slate-500">
                                                                    {m.preview}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                            {tChatNext && (
                                                <button
                                                    type="button"
                                                    onClick={() => loadTChatMsgs('more')}
                                                    className="text-sm"
                                                >
                                                    More
                                                </button>
                                            )}
                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={importTChatBundle}
                                                    className="px-4 py-2 bg-slate-800 text-white rounded-xl"
                                                >
                                                    Import selected (one document)
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default IntegrationsTab;
